import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  EmptyState,
  Input,
  ReportTable,
  type ReportTablePagination,
  type ReportTableSort,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { ArrowLeft, Download } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { ReportLink } from '../components/reporting/ReportLink';
import {
  exportInsuranceEligibilityReportReportsInsuranceEligibilityExportGet,
  getInsuranceEligibilityOptionsReportsInsuranceEligibilityOptionsGet,
  getInsuranceEligibilityReportReportsInsuranceEligibilityGet,
} from '../services/api/sdk.gen';
import type {
  InsuranceEligibilityOption,
  InsuranceEligibilityReportResult,
  InsuranceEligibilityReportRow,
  InsuranceEligibilitySortBy,
} from '../services/api/types.gen';
import { PermissionSlug } from '../constants/featureFlags';
import ReportsPermissionAlert from './ReportsPermissionAlert';
import { useReportsBackHref } from '../reporting/backHref';
import { useStore } from '../store';
import { useDebounce } from '../hooks/useDebounce';
import { formatDateTimeInAgencyZone } from '../utils/dateUtils';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import {
  coerceReportPageSize,
  formatReportEmptyValue,
  readOptionalChoiceParam,
  readPositiveIntegerParam,
  REPORT_PAGE_SIZE_OPTIONS,
} from '../utils/reportDisplay';

const ALL_VALUE = '__all__';

type SortBy = InsuranceEligibilitySortBy;
type SortOrder = 'asc' | 'desc';
type ReportRow = InsuranceEligibilityReportRow;
type Option = InsuranceEligibilityOption;

const VALID_SORTS: SortBy[] = [
  'patient_name',
  'mrn',
  'clinic',
  'payer',
  'status',
  'last_checked_at',
];
const VALID_DIRECTIONS: SortOrder[] = ['asc', 'desc'];

const columns = [
  {
    id: 'patient_name',
    header: 'Patient Name',
    accessor: (row: ReportRow) => row.patient_name,
    render: (_value: unknown, row: ReportRow) => (
      <ReportLink to={`/patients/${row.patient_id}/insurance`}>
        {row.patient_name}
      </ReportLink>
    ),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'mrn',
    header: 'MRN',
    accessor: (row: ReportRow) => formatReportEmptyValue(row.mrn),
    enableSorting: true,
    minWidth: 100,
  },
  {
    id: 'clinic',
    header: 'Clinic',
    accessor: (row: ReportRow) => formatReportEmptyValue(row.clinic),
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'payer',
    header: 'Payer',
    accessor: (row: ReportRow) => formatReportEmptyValue(row.payer),
    enableSorting: true,
    minWidth: 160,
  },
  {
    id: 'policy_number',
    header: 'Policy #',
    accessor: (row: ReportRow) => formatReportEmptyValue(row.policy_number),
    enableSorting: false,
    minWidth: 120,
  },
  {
    id: 'status',
    header: 'Eligibility Status',
    accessor: (row: ReportRow) => row.status_label,
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'last_checked_at',
    header: 'Last Checked',
    accessor: (row: ReportRow) =>
      // Stored as UTC; shown in the agency's zone, as the chart shows it.
      formatReportEmptyValue(
        formatDateTimeInAgencyZone(row.last_checked_at) || null
      ),
    enableSorting: true,
    minWidth: 140,
  },
];

function FilterSelect({
  label,
  value,
  options,
  allLabel,
  onChange,
}: {
  label: string;
  value: string;
  options: Option[];
  allLabel: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className='space-y-1 text-sm font-medium'>
      {label}
      <Select
        value={value || ALL_VALUE}
        onValueChange={next => onChange(next === ALL_VALUE ? '' : next)}
      >
        <SelectTrigger selectSize='default'>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_VALUE}>{allLabel}</SelectItem>
          {options
            .filter(option => option.id.trim() !== '')
            .map(option => (
              <SelectItem key={option.id} value={option.id}>
                {option.label}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </label>
  );
}

export default function InsuranceEligibilityReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const reportsBackHref = useReportsBackHref();
  const { hasPermission, showError } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
      showError: state.showError,
    }))
  );

  const clinic = searchParams.get('clinic') ?? '';
  const status = searchParams.get('status') ?? '';
  const includeTestRows = searchParams.get('include_test_rows') === 'true';
  const q = searchParams.get('q') ?? '';
  const [searchText, setSearchText] = useState(q);
  // The typed search reaches the URL (and so the query) after a debounce. Until
  // it does, Export would download the previous search, not the one on screen.
  const isSearchPending = searchText !== q;
  const page = readPositiveIntegerParam(searchParams.get('page'));
  const pageSize = coerceReportPageSize(Number(searchParams.get('size')));
  const sortBy = readOptionalChoiceParam(searchParams.get('sort'), VALID_SORTS);
  const sortOrder = readOptionalChoiceParam(
    searchParams.get('dir'),
    VALID_DIRECTIONS
  );

  const [report, setReport] = useState<InsuranceEligibilityReportResult | null>(
    null
  );
  const [options, setOptions] = useState<{
    clinics: Option[];
    statuses: Option[];
  }>({ clinics: [], statuses: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const canAccess = hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS);

  const updateParams = useCallback(
    (updates: Record<string, string | number | boolean | null>) => {
      setSearchParams(current => {
        const params = new URLSearchParams(current);
        Object.entries(updates).forEach(([key, value]) => {
          if (value === null || value === '' || value === false) {
            params.delete(key);
          } else {
            params.set(key, String(value));
          }
        });
        return params;
      });
    },
    [setSearchParams]
  );

  const debouncedUpdateSearch = useDebounce((value: string) => {
    updateParams({ q: value || null, page: 1 });
  }, 350);

  useEffect(() => {
    setSearchText(q);
  }, [q]);

  useEffect(() => {
    if (!canAccess) return;
    let cancelled = false;
    getInsuranceEligibilityOptionsReportsInsuranceEligibilityOptionsGet()
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          showError('Unable to load report filters. Please try again.');
          return;
        }
        setOptions(response.data);
      })
      .catch(error => {
        console.error('Failed to load insurance eligibility options', error);
        if (!cancelled) {
          showError(
            getBusinessErrorMessage(error) ??
              'Unable to load report filters. Please try again.'
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [canAccess, showError]);

  useEffect(() => {
    if (!canAccess) return;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(false);
    setReport(null);
    getInsuranceEligibilityReportReportsInsuranceEligibilityGet({
      query: {
        clinic_id: clinic || null,
        statuses: status || null,
        include_test_rows: includeTestRows,
        q: q || null,
        sort_by: sortBy ?? null,
        sort_order: sortOrder ?? null,
        page,
        page_size: pageSize,
      },
    })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          setLoadError(true);
          showError(
            'Unable to load insurance eligibility report. Please try again.'
          );
          return;
        }
        setReport(response.data);
      })
      .catch(error => {
        console.error('Failed to load insurance eligibility report', error);
        if (!cancelled) {
          setLoadError(true);
          showError(
            getBusinessErrorMessage(error) ??
              'Unable to load insurance eligibility report. Please try again.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    canAccess,
    clinic,
    status,
    includeTestRows,
    q,
    sortBy,
    sortOrder,
    page,
    pageSize,
    showError,
  ]);

  const sorting = useMemo<ReportTableSort | null>(
    () => (sortBy ? { id: sortBy, direction: sortOrder ?? 'asc' } : null),
    [sortBy, sortOrder]
  );
  const pagination = useMemo<ReportTablePagination>(
    () => ({
      page,
      pageSize,
      total: report?.total ?? 0,
      totalPages: Math.ceil((report?.total ?? 0) / pageSize),
      pageSizeOptions: REPORT_PAGE_SIZE_OPTIONS,
    }),
    [page, pageSize, report?.total]
  );

  const exportReport = async () => {
    setIsExporting(true);
    try {
      const response =
        await exportInsuranceEligibilityReportReportsInsuranceEligibilityExportGet(
          {
            query: {
              clinic_id: clinic || null,
              statuses: status || null,
              include_test_rows: includeTestRows,
              q: q || null,
              sort_by: sortBy ?? null,
              sort_order: sortOrder ?? null,
            },
            responseType: 'blob',
          }
        );
      if (response.error || !response.data) {
        showError(
          await getUserFacingApiMessageAsync(
            response.error,
            'Unable to download insurance eligibility report. Please try again.'
          )
        );
        return;
      }
      downloadResponseAsFile(
        response as never,
        'RogerInsuranceEligibility.xlsx'
      );
    } catch (error) {
      console.error('Failed to download insurance eligibility report', error);
      showError(
        await getUserFacingApiMessageAsync(
          error,
          'Unable to download insurance eligibility report. Please try again.'
        )
      );
    } finally {
      setIsExporting(false);
    }
  };

  if (!canAccess) return <ReportsPermissionAlert />;

  const emptyState = loadError ? (
    <EmptyState title='Unable to load report' description='Please try again.' />
  ) : (
    <EmptyState
      title='No patients found'
      description='Adjust filters and try again.'
    />
  );

  return (
    <div className='space-y-6'>
      <Link
        to={reportsBackHref}
        className='inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground'
      >
        <ArrowLeft className='mr-1 h-4 w-4' /> Reports
      </Link>
      <div className='flex flex-wrap items-start gap-3'>
        <Button
          variant='outline'
          size='sm'
          onClick={() => void exportReport()}
          disabled={isExporting || isLoading || isSearchPending}
        >
          <Download className='h-4 w-4' /> Export
        </Button>
      </div>

      <Alert>
        <AlertDescription>
          Failed and inactive coverage sort first by default. Enable weekly
          auto-checks under Admin → Insurance.
        </AlertDescription>
      </Alert>

      <div className='flex flex-wrap items-end gap-3'>
        <label className='space-y-1 text-sm font-medium'>
          Search
          <Input
            value={searchText}
            onChange={e => {
              setSearchText(e.target.value);
              debouncedUpdateSearch(e.target.value);
            }}
            placeholder='Patient, MRN, payer…'
            className='w-56'
          />
        </label>
        <FilterSelect
          label='Clinic'
          value={clinic}
          options={options.clinics}
          allLabel='All clinics'
          onChange={value => updateParams({ clinic: value || null, page: 1 })}
        />
        <FilterSelect
          label='Status'
          value={status}
          options={options.statuses}
          allLabel='All statuses'
          onChange={value => updateParams({ status: value || null, page: 1 })}
        />
        <label className='flex items-center gap-2 text-sm pb-2'>
          <input
            type='checkbox'
            checked={includeTestRows}
            onChange={e =>
              updateParams({
                include_test_rows: e.target.checked || null,
                page: 1,
              })
            }
          />
          Include test patients
        </label>
      </div>

      <ReportTable
        data={report?.items ?? []}
        columns={columns}
        getRowId={row => row.row_id}
        caption='Insurance eligibility report results'
        isLoading={isLoading}
        emptyState={emptyState}
        sorting={sorting}
        onSortingChange={next => {
          if (!next) {
            updateParams({ sort: null, dir: null, page: 1 });
            return;
          }
          updateParams({
            sort: next.id,
            dir: next.direction,
            page: 1,
          });
        }}
        pagination={pagination}
        onPaginationChange={next => {
          updateParams({
            page: next.page,
            size: next.pageSize,
          });
        }}
      />
    </div>
  );
}
